import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { getAdminStats, getAdminTrends, getSystemStatus } from "../../services/ledgerService";
import SparkBars from "../../components/SparkBars";

const StatWithTrend = ({ label, value, series, barColor, onClick }) => (
    <Card
        className="h-100"
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
    >
        <Card.Body>
            <Card.Subtitle className="text-muted mb-2 small text-uppercase">{label}</Card.Subtitle>
            <Card.Title style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>
                {value ?? "—"}
            </Card.Title>
            {series ? (
                <SparkBars data={series} barColor={barColor} />
            ) : (
                <div style={{ height: 60 }} className="d-flex align-items-center justify-content-center">
                    <Spinner animation="border" size="sm" />
                </div>
            )}
        </Card.Body>
    </Card>
);

// "37 of 45" as a percent, or "—" when the denominator is zero (no
// active users in the window) so we don't claim 0% / NaN%.
function pct(numer, denom) {
    if (!denom) return "—";
    return `${Math.round((numer / denom) * 1000) / 10}%`;
}

const TermsAcceptanceCard = ({ stats }) => {
    if (!stats) {
        return (
            <Card className="h-100">
                <Card.Body className="d-flex align-items-center justify-content-center">
                    <Spinner animation="border" size="sm" />
                </Card.Body>
            </Card>
        );
    }
    const activeAccepted = stats.activeHostsAcceptedTerms ?? 0;
    // Denominator must share the numerator's 3-month window, otherwise the
    // rate can exceed 100% (activeHostsLastMonth is a 1-month count).
    const activeTotal = stats.activeHostsLast3Months ?? 0;
    const allAccepted = stats.usersAcceptedTerms ?? 0;
    const allTotal = stats.totalUsers ?? 0;
    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Subtitle className="text-muted mb-2 small text-uppercase">
                    Terms accepted (active users)
                </Card.Subtitle>
                <Card.Title style={{ fontSize: "2.5rem", marginBottom: "0.25rem" }}>
                    {pct(activeAccepted, activeTotal)}
                </Card.Title>
                <div className="text-muted small mb-3">
                    {activeAccepted} of {activeTotal} active in last 3 months
                </div>
                <hr className="my-2" />
                <div className="small">
                    <span className="text-muted">All users:</span>{" "}
                    <strong>{pct(allAccepted, allTotal)}</strong>{" "}
                    <span className="text-muted">({allAccepted} / {allTotal})</span>
                </div>
                {stats.termsAcceptanceVersion && (
                    <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>
                        v{stats.termsAcceptanceVersion}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

const Section = ({ title, children }) => (
    <section className="mb-4">
        <h2 className="h5 text-muted mb-3 text-uppercase">{title}</h2>
        {children}
    </section>
);

const Spin = () => (
    <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
);

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [trends, setTrends] = useState(null);
    const [status, setStatus] = useState(null);
    const [statsErr, setStatsErr] = useState(null);
    const [trendsErr, setTrendsErr] = useState(null);
    const [statusErr, setStatusErr] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        getAdminStats().then(setStats).catch((e) => setStatsErr(e.message));
        getAdminTrends(12).then(setTrends).catch((e) => setTrendsErr(e.message));
        getSystemStatus().then(setStatus).catch((e) => setStatusErr(e.message));
    }, []);

    const dbConn = status?.dbConnection || {};

    return (
        <Container className="py-4 admin-page">
            <h1 className="mb-4">Admin</h1>

            <Section title="Capabilities">
                <Row className="g-3">
                    <Col md={4}>
                        <Card as={Link} to="/admin/houses" className="text-decoration-none text-reset h-100">
                            <Card.Body>
                                <Card.Title>Houses</Card.Title>
                                <Card.Text className="text-muted small mb-0">
                                    Browse every house. View as (read-only) or log in as the owner for full impersonation.
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card as={Link} to="/admin/stats/created-games" className="text-decoration-none text-reset h-100">
                            <Card.Body>
                                <Card.Title>Games created</Card.Title>
                                <Card.Text className="text-muted small mb-0">
                                    Drill into every game created in a chosen window with player counts and totals.
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card as={Link} to="/admin/email" className="text-decoration-none text-reset h-100">
                            <Card.Body>
                                <Card.Title>Email campaign</Card.Title>
                                <Card.Text className="text-muted small mb-0">
                                    Compose and send a design-system email to a host segment, with live preview and per-recipient tracking.
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card as={Link} to="/admin/surveys" className="text-decoration-none text-reset h-100">
                            <Card.Body>
                                <Card.Title>Survey responses</Card.Title>
                                <Card.Text className="text-muted small mb-0">
                                    Read usage and churn survey answers collected from campaign emails, with average ratings.
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Section>

            <Section title="Business stats">
                {statsErr ? (
                    <div className="text-danger">Error: {statsErr}</div>
                ) : (
                    <Row className="g-3">
                        <Col md={6} lg={3}>
                            <StatWithTrend
                                label="Games created (last month)"
                                value={stats ? stats.gamesCreatedLastMonth : <Spin />}
                                series={trends?.gamesCreated}
                                barColor="var(--accent)"
                                onClick={() => navigate("/admin/stats/created-games")}
                            />
                        </Col>
                        <Col md={6} lg={3}>
                            <StatWithTrend
                                label="New accounts (last month)"
                                value={stats ? stats.newAccountsLastMonth : <Spin />}
                                series={trends?.newAccounts}
                                barColor="var(--up)"
                                onClick={() => navigate("/admin/stats/new-accounts")}
                            />
                        </Col>
                        <Col md={6} lg={3}>
                            <StatWithTrend
                                label="Active hosts (last month)"
                                value={stats ? stats.activeHostsLastMonth : <Spin />}
                                series={trends?.activeHosts}
                                barColor="var(--gold)"
                                onClick={() => navigate("/admin/stats/active-hosts")}
                            />
                        </Col>
                        <Col md={6} lg={3}>
                            <TermsAcceptanceCard stats={stats} />
                        </Col>
                    </Row>
                )}
                {trendsErr && (
                    <div className="text-warning small mt-2">
                        Trend data unavailable: {trendsErr}
                    </div>
                )}
            </Section>

            <Section title="App status">
                {statusErr ? (
                    <div className="text-danger">Error: {statusErr}</div>
                ) : !status ? (
                    <Spin />
                ) : (
                    <Row className="g-3">
                        <Col md={4}>
                            <Card className="h-100">
                                <Card.Body>
                                    <Card.Subtitle className="text-muted mb-2 small text-uppercase">Version</Card.Subtitle>
                                    <code className="d-block text-break">{status.version || "unavailable"}</code>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="h-100">
                                <Card.Body>
                                    <Card.Subtitle className="text-muted mb-2 small text-uppercase">Runtime</Card.Subtitle>
                                    <Badge bg={status.runtime === "Lambda" ? "primary" : "secondary"}>{status.runtime}</Badge>
                                    <span className="ms-2 text-success">{status.status}</span>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="h-100">
                                <Card.Body>
                                    <Card.Subtitle className="text-muted mb-2 small text-uppercase">DB connections</Card.Subtitle>
                                    <div className="small">
                                        <div>Open: <strong>{dbConn.OpenConnections ?? "?"}</strong></div>
                                        <div>In use: {dbConn.InUse ?? "?"} · Idle: {dbConn.Idle ?? "?"}</div>
                                        <div className="text-muted">Max open: {dbConn.MaxOpenConnections ?? "?"}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Section>
        </Container>
    );
};

export default AdminDashboard;
